/** Entry lists for the screens: newest first, voided ones included only when asked. */
import {
  ALL_TIME,
  addMonths,
  inRange,
  isVoided,
  monthRange,
  type DateRange,
  type Entry,
  type State,
} from '@khata/core';
import type { ListPeriod } from './ui.js';

export interface ListRow<T extends Entry> {
  readonly entry: T;
  readonly voided: boolean;
}

/** Newest first: by business date, then by append order. */
export function byDateDesc(a: Entry, b: Entry): number {
  return b.date < a.date ? -1 : b.date > a.date ? 1 : b.seq - a.seq;
}

/** Date range for a list period, given today's date. */
export function periodRange(period: ListPeriod, today: string): DateRange {
  const thisM = today.slice(0, 7);
  switch (period.kind) {
    case 'this':
      return monthRange(thisM);
    case 'last':
      return monthRange(addMonths(thisM, -1));
    case 'month':
      return monthRange(period.month);
    case 'all':
      return ALL_TIME;
  }
}

export function listEntries<T extends Entry>(
  state: State,
  entries: ReadonlyArray<Entry>,
  pick: (e: Entry) => e is T,
  includeVoided: boolean,
  range: DateRange = ALL_TIME,
): ListRow<T>[] {
  const rows: ListRow<T>[] = [];
  for (const e of entries) {
    if (e.type === 'void' || !pick(e)) continue;
    if (!inRange(e.date, range)) continue;
    const voided = isVoided(state, e.id);
    if (voided && !includeVoided) continue;
    rows.push({ entry: e, voided });
  }
  return rows.sort((a, b) => byDateDesc(a.entry, b.entry));
}

/** Case-insensitive "every word must appear somewhere" match over a row's searchable text. */
export function matches(text: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = text.toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}

/** Months that have at least one entry, newest first. */
export function monthsWithEntries(entries: ReadonlyArray<Entry>): string[] {
  const set = new Set<string>();
  for (const e of entries) if (e.type !== 'void') set.add(e.date.slice(0, 7));
  return [...set].sort().reverse();
}
